// src/app/script-loader.service.ts
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

interface ScriptStatus {
  loaded: boolean;
  loading: boolean;
  promise: Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {
  private scripts: Record<string, ScriptStatus> = {};

  constructor(@Inject(DOCUMENT) private document: Document) {}

  load(url: string): Promise<void> {
    // If we’ve already kicked off loading, just return the same promise
    if (this.scripts[url]) {
      return this.scripts[url].promise;
    }

    // Otherwise, create the <script> tag and start loading
    const scriptEl = this.document.createElement('script');
    scriptEl.src = url;
    scriptEl.async = true;

    let res: () => void, rej: (e: any) => void;
    const promise = new Promise<void>((resolve, reject) => {
      res = resolve;
      rej = reject;
    });

    this.scripts[url] = {
      loaded: false,
      loading: true,
      promise,
    };

    scriptEl.onload = () => {
      this.scripts[url].loaded = true;
      this.scripts[url].loading = false;
      res();
    };

    scriptEl.onerror = (err) => {
      this.scripts[url].loaded = false;
      this.scripts[url].loading = false;
      rej(err);
    };

    this.document.head.appendChild(scriptEl);
    return promise;
  }

  isLoaded(url: string): boolean {
    return !!this.scripts[url]?.loaded;
  }

  isLoading(url: string): boolean {
    return !!this.scripts[url]?.loading;
  }
}
